<template>
    <PageSidebar>
        <template #sidebarTitle>
            {{ $t("pricing.menu.product") }}
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <NotificationAlert
                    v-if="pricing.ecId"
                    :subtitle="$t('pricing.product.already_synced')"
                    :close-button="false"
                />
                <template v-else>
                    <!-- step 1  -->
                    <template v-if="currentStep === 0">
                        <p class="ct-text">{{ $t("pricing.product.info") }}</p>
                        <template v-if="!showNotATenant">
                            <ButtonPrimaryMd @click="getECData" class="w-full">
                                {{ $t("pricing.product.get_products") }}
                            </ButtonPrimaryMd>
                            <ButtonSecondaryMd @click="addProduct" class="w-full">
                                <AddIcon />
                                <span>{{ $t("pricing.product.add_new_product") }}</span>
                            </ButtonSecondaryMd>
                        </template>

                        <template v-else>
                            <NotificationAlert
                                :subtitle="$t('pricing.product.not_tenant')"
                                :close-button="false"
                            />

                            <CardEmpty
                                class="w-full"
                                :title="$t('pricing.product.add_new_product')"
                                :subtitle="$t('pricing.product.first_create_product')"
                                :icon="emptyIcon"
                                :ctaLabel="$t('pricing.product.create_product')"
                                @add="addProduct"
                            />
                        </template>
                    </template>
                    <!-- step 2 -->
                    <template v-if="currentStep === 1">
                        <p class="ct-text">
                            {{ $t("pricing.product.chose_type") }}
                        </p>

                        <ProductCard :item="selectAllProduct" @click="selectAll" />

                        <p class="ct-text">
                            {{ $t("pricing.product.single_product") }}
                        </p>

                        <ProductCard
                            v-for="item in list"
                            :key="item.id"
                            :item="item"
                            @click="selectProduct(item)"
                        />

                        <CardEmpty
                            v-if="!list.length"
                            class="w-full"
                            :title="$t('pricing.product.add_new_product')"
                            :subtitle="$t('pricing.product.first_create_product')"
                            :icon="emptyIcon"
                            :ctaLabel="$t('pricing.product.create_product')"
                            @add="addProduct"
                        />
                    </template>
                    <!-- step 3  -->
                    <template v-if="currentStep === 2">
                        <ProductCard :item="selectedProduct" isSelected />

                        <FormVariants
                            :title="$t('pricing.product.sync')"
                            :variants="syncVariants"
                            v-model="syncMode"
                        />

                        <ButtonPrimaryMd @click="importData" class="w-full">
                            <span>{{ $t("pricing.product.import_data") }}</span>
                        </ButtonPrimaryMd>
                    </template>
                </template>
            </div>
        </template>
    </PageSidebar>
</template>

<script>
import { PageSidebar } from "@easy/ui/page-components";
import { mapState, mapGetters, mapActions } from "vuex";
import { ButtonPrimaryMd, ButtonSecondaryMd } from "@easy/ui/buttons";
import { CardEmpty } from "@easy/ui/cards";
import { ShippingBoxIcon, AddIcon } from "@easy/ui/icons";
import ProductCard from "./product/ProductCard.vue";
import { FormVariants } from "@easy/ui/forms";
import { NotificationAlert } from "@easy/ui/notifications";

import { makeLogger } from "@/shared/consola";
import { userService } from "@/shared/user/user.service";
import { PRICING_ROUTE_NAMES } from "@/apps/pricing/pricing.routes";

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const logger = makeLogger("pricing sidebar Product");

export default {
    name: "SidebarProduct",
    components: {
        PageSidebar,
        ButtonPrimaryMd,
        CardEmpty,
        ProductCard,
        FormVariants,
        NotificationAlert,
        ButtonSecondaryMd,
        AddIcon,
    },
    data() {
        return {
            showNotATenant: false,
            syncMode: 1,
            tenantSlug: "",
            productSlug: "",
            list: [],
            dataReady: false,
            productIsSelected: false,
            selectedProduct: {},

            currentStep: 0,
            // complete / current / upcomming
            steps: [
                { name: "Step 1", status: "current" },
                { name: "Step 2", status: "upcomming" },
                { name: "Step 3", status: "upcomming" },
            ],
        };
    },
    computed: {
        ...mapState("pricing", ["pricing", "categories", "hasChanges"]),
        ...mapGetters(["easyUrls"]),
        syncVariants() {
            return [
                {
                    value: 1,
                    label: this.$t("pricing.product.sync_on"),
                    info: `<div class='mt-1 space-y-1'><p>${this.$t("pricing.product.sync_pro_1")}</p> <p>${this.$t("pricing.product.sync_pro_2")}</p> <p>${this.$t("pricing.product.sync_pro_3")}</p></div>`,
                },
                {
                    value: 0,
                    label: this.$t("pricing.product.sync_off"),
                    info: `<div class='mt-1 space-y-1'><p>${this.$t("pricing.product.no_sync_pro_1")}</p> <p>${this.$t("pricing.product.no_sync_pro_2")}</p> <p>${this.$t("pricing.product.no_sync_pro_3")}</p></div>`,
                },
            ];
        },
        emptyIcon() {
            return ShippingBoxIcon;
        },
        selectAllProduct() {
            return {
                name: this.$t("pricing.product.select_name"),
                description: this.$t("pricing.product.select_description"),
                photo_url: `${process.env.VUE_APP_ORIGIN}/assets/easycart.png`,
            };
        },
    },
    watch: {
        currentStep: function (value) {
            this.setStep(value);
        },
    },
    methods: {
        ...mapActions(["showServerError"]),
        ...mapActions("pricing", ["getECProducts", "addAllECProducts", "addECProducts"]),
        async getECData() {
            const notTenantErrors = [
                "ERR_INT_EASY_CART_NOT_A_TENANT",
                "ERR_INT_EASY_CART_USER_NOT_FOUND",
            ];

            const ecData = await this.getECProducts();
            if (!ecData) {
                return;
            }
            if (ecData?.message && ecData.message === "SUCCESS") {
                this.list = ecData.result?.products;
                this.dataReady = true;
                this.currentStep = 1;
            } else if (ecData?.message && notTenantErrors.includes(ecData.message)) {
                this.showNotATenant = true;
            } else {
                this.showServerError(ecData.message);
            }
        },
        setStep(index) {
            this.steps.slice(0, index).forEach((step) => {
                step.status = "complete";
            });
            this.steps[index].status = "current";
            this.steps.slice(index + 1, this.steps.length).forEach((step) => {
                step.status = "upcomming";
            });
        },
        selectAll() {
            this.currentStep = 2;
            this.selectedProduct = this.selectAllProduct;
        },
        selectProduct(item) {
            this.currentStep = 2;
            this.selectedProduct = item;
        },
        async importData() {
            await this.addECProducts({
                ecId: this.selectedProduct.id
                    ? this.selectedProduct.id //ecProductId
                    : this.list[0]?.tenant_id, // or ecTenantId
                isSingleProduct: !!this.selectedProduct.id,
                mode: this.syncMode ? "sync" : "async",
            });
            this.$router.push({ name: PRICING_ROUTE_NAMES.projectVariants });
        },
        goTo(index) {
            this.currentStep = index;
        },
        async addProduct() {
            const newProductLink = `${this.easyUrls.cart}creator/products/new`;
            const { magicUrl } = await userService.forgeMagicLink(newProductLink);
            window.location = magicUrl;
        },
    },
};
</script>
